import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, Popconfirm, Modal, Form, Switch, Tag, Tooltip, Alert } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getBundles, createBundle, updateBundle, deleteBundle, getAllAssets } from '../../services/api';
import toast from 'react-hot-toast';
import './BundleManagement.scss';
import AssetSelector2 from './AssetSelector';
import  typeOptions  from '../utils/typeOptions';

const BundleManagement = ({ setTitle }) => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    setTitle('EarthVerse CRM - Bundle管理');
    fetchBundles();
  }, [setTitle]);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const fetchedBundles = await getBundles();
      setBundles(fetchedBundles);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to fetch bundles. Please try again.');
      setLoading(false);
    }
  };

  const fetchAllAssets = async () => {
    try {
      setAssetLoading(true);
      const assets = await getAllAssets();
      setAvailableAssets(assets);
      setAssetLoading(false);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to fetch assets. Please try again.');
      setAssetLoading(false);
    }
  };

  const handleAddOrUpdateBundle = async (values) => {
    try {
      const bundleData = {
        ...values,
        assets: selectedAssets.map((asset, index) => ({
          id: asset.id,
          order: index // 新增 order 欄位，使用目前陣列索引表示順序
        }))
      };

      if (editingBundle) {
        if (bundleData.assets.length === 0) {
          toast.error('請至少指定一個Asset給這個bundle');
          return;
        }
        await updateBundle(editingBundle.id, bundleData);
        toast.success('Bundle updated successfully!');
      } else {
        bundleData.active = bundleData.active || false;
        bundleData.type = bundleData.type || 0;

        await createBundle(bundleData);
        toast.success('Bundle added successfully!');
      }
      setIsModalVisible(false);
      setEditingBundle(null);
      form.resetFields();
      setSelectedAssets([]);
      fetchBundles();
    } catch (error) {
      console.error('Error adding/updating bundle:', error);
      toast.error('Failed to add/update bundle. Please try again.');
    }
  };

  const handleDeleteBundle = async (bundleId) => {
    try {
      await deleteBundle(bundleId);
      toast.success('Bundle deleted successfully!');
      fetchBundles();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      toast.error('Failed to delete bundle. Please try again.');
    }
  };

  const showModal = async (bundle = null) => {
    await fetchAllAssets();
    setEditingBundle(bundle);
    if (bundle) {
      form.setFieldsValue(bundle);
      const bundleAssets = bundle.assets.map(asset => asset.asset[0]);
      setSelectedAssets(bundleAssets);
    } else {
      form.resetFields();
      setSelectedAssets([]);
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingBundle(null);
    form.resetFields();
    setSelectedAssets([]);
  };

  const onAssetsChange = useCallback((newSelectedAssets) => {
    setSelectedAssets(newSelectedAssets);
  }, []);

  const getAssetTooltipContent = (asset) => {
    return (
      <div>
        <p><strong>ID:</strong> {asset.id}</p>
        <p><strong>Name:</strong> {asset.name}</p>
        <p><strong>Type:</strong> {typeOptions.find(option => option.value === asset.type)?.label || asset.type}</p>
        <p><strong>URI:</strong></p>
        <p>{asset.uri}</p>
        <p><strong>Last Updated:</strong></p>
        <p>{new Date(asset.last_updated).toLocaleString('zh-TW')}</p>
      </div>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 60,
      align: 'center',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      align: 'center',
      render: (active) => (active ? '✅' : ''),
    },
    {
      title: 'Creator',
      dataIndex: 'created_by',
      key: 'created_by',
      render: (createdBy) => createdBy || '-',
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
      sorter: (a, b) => new Date(a.last_updated) - new Date(b.last_updated),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200, // 固定寬度
      ellipsis: true, // 自動省略超出部分
      render: (description) => description || '-',
    },
    {
      title: 'Assets',
      dataIndex: 'assets',
      key: 'assets',
      width: 300, // 固定寬度
      ellipsis: true, // 自動省略超出部分
      render: (assets) => (
        <>
          {assets.map((asset) => (
            <Tooltip key={asset.id} title={getAssetTooltipContent(asset.asset[0])} placement="top">
              <Tag color="blue">
                {asset.asset[0].name} ({typeOptions.find(option => option.value === asset.asset[0].type)?.label || asset.asset[0].type})
              </Tag>
            </Tooltip>
          ))}
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)} style={{ marginRight: 8 }} />
          <Popconfirm
            title="Are you sure you want to delete this bundle?"
            onConfirm={() => handleDeleteBundle(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  const filteredBundles = bundles.filter(bundle =>
    bundle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (bundle.type && bundle.type.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (bundle.created_by && bundle.created_by.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="bundle-management-container">
      <h2>Bundle管理</h2>
      <div className="bundle-actions">
        <Input
          placeholder="Search bundles..."
          prefix={<SearchOutlined />}
          suffix={
            searchTerm ? (
              <CloseCircleOutlined onClick={() => setSearchTerm('')} style={{ cursor: 'pointer' }} />
            ) : null
          }
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 200 }}
        />

        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          Add New Bundle
        </Button>
      </div>
      <Table
        dataSource={filteredBundles}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 30 }}
        loading={loading}
      />
      <Modal
        title={editingBundle ? "Edit Bundle" : "Add New Bundle"}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="bundle-modal"
        width={1200}
      >
        <Form form={form} onFinish={handleAddOrUpdateBundle} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please input the bundle name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Input />
          </Form.Item>
          <Alert message="Type為保留欄位，暫可不填" type="info" style={{ marginBottom: '20px' }} />
          <Form.Item name="description" label="Description">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="asset-selector" label="Assets">
            <AssetSelector2
              key={isModalVisible ? 'modal-open' : 'modal-closed'}
              availableAssets={availableAssets}
              selectedAssets={selectedAssets}
              onAssetsChange={onAssetsChange} 
              loading={assetLoading}
            />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: '100%', height: '40px' }}>
              {editingBundle ? "Update Bundle" : "Add Bundle"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BundleManagement;